import React, { useState } from 'react';
import { Sticker, Scissors, Layout, Type, Sparkles, Search } from 'lucide-react';
import { DoodleStrip } from './Doodles';

// ============== STICKERS DATA ==============
const STICKER_CATEGORIES = {
  animals: { label: 'Animals', stickers: ['🐱','🐶','🐸','🦊','🐼','🐻','🐰','🦉','🐝','🦋','🐢','🐙','🦄','🐧','🐨'] },
  nature: { label: 'Nature', stickers: ['🌸','🌻','🌺','🍀','🌿','🌵','🌳','🍁','🌾','🌈','☀️','🌙','⭐','💧','🔥'] },
  food: { label: 'Food', stickers: ['☕','🍰','🧁','🍩','🍪','🍓','🍑','🍕','🌮','🍜','🧋','🍫','🥐','🫖','🍯'] },
  hearts: { label: 'Hearts', stickers: ['❤️','🧡','💛','💚','💙','💜','🤎','🖤','🤍','💖','💝','💗','💓','💕','💞'] },
  weather: { label: 'Weather', stickers: ['🌧️','⛅','🌤️','❄️','🌪️','🌊','🌸','🍂','🌺','☁️','⚡','🌙','🌟','✨','💫'] },
  deco: { label: 'Deco', stickers: ['🎀','🎵','📌','✏️','📎','🔖','🏷️','💌','📝','🎨','✨','💎','👑','🌟','🎪'] },
};

// ============== WASHI TAPE DATA ==============
const WASHI_TAPES = [
  { id: 'dots_pink', pattern: 'repeating-linear-gradient(90deg, #FFC8DD 0px, #FFC8DD 8px, #FFE0EE 8px, #FFE0EE 16px)', label: 'Pink Dots' },
  { id: 'stripe_blue', pattern: 'repeating-linear-gradient(45deg, #A2D2FF 0px, #A2D2FF 6px, #C8E4FF 6px, #C8E4FF 12px)', label: 'Blue Stripe' },
  { id: 'check_green', pattern: 'repeating-conic-gradient(#B9FBC0 0% 25%, #D5FDD8 0% 50%) 50% / 12px 12px', label: 'Green Check' },
  { id: 'zigzag_purple', pattern: 'repeating-linear-gradient(135deg, #CDB4DB 0px, #CDB4DB 6px, #E0D0E8 6px, #E0D0E8 12px)', label: 'Purple Zigzag' },
  { id: 'solid_yellow', pattern: 'linear-gradient(90deg, #FDF0D5, #F5E6C0)', label: 'Warm Yellow' },
  { id: 'rainbow', pattern: 'linear-gradient(90deg, #FF6B6B, #FFC8DD, #FDF0D5, #B9FBC0, #A2D2FF, #CDB4DB)', label: 'Rainbow' },
  { id: 'dots_mint', pattern: 'radial-gradient(circle, #70C080 2px, #B9FBC0 2px) 0 0 / 10px 10px', label: 'Mint Dots' },
  { id: 'stripe_coral', pattern: 'repeating-linear-gradient(0deg, #FF6B6B 0px, #FF6B6B 4px, #FFB0B0 4px, #FFB0B0 8px)', label: 'Coral Stripe' },
];

// ============== TEMPLATES DATA ==============
const JOURNAL_TEMPLATES = [
  { id: 'daily', name: 'Daily Planner', icon: '📋', color: '#A2D2FF',
    sections: ['Morning Routine', 'Top 3 Tasks', 'Notes', 'Gratitude', 'Evening Reflection'] },
  { id: 'gratitude', name: 'Gratitude Log', icon: '🙏', color: '#B9FBC0',
    sections: ['3 Things I\'m Grateful For', 'Best Moment Today', 'Kind Act I Did', 'What Made Me Smile'] },
  { id: 'weekly', name: 'Weekly Review', icon: '📊', color: '#FFC8DD',
    sections: ['Wins This Week', 'Challenges', 'Lessons Learned', 'Goals for Next Week', 'Self-Care Check'] },
  { id: 'braindump', name: 'Brain Dump', icon: '🧠', color: '#CDB4DB',
    sections: ['Thoughts', 'Ideas', 'Worries', 'To Remember', 'Random'] },
  { id: 'moodboard', name: 'Mood Board', icon: '🎨', color: '#FDF0D5',
    sections: ['How I Feel', 'Colors of Today', 'Inspiration', 'Energy Level', 'Music Mood'] },
  { id: 'selfcare', name: 'Self-Care Check', icon: '💆', color: '#FFC8DD',
    sections: ['Body (Exercise/Sleep)', 'Mind (Reading/Learning)', 'Heart (Relationships)', 'Soul (Hobbies/Rest)', 'Water & Food'] },
];

// ============== TEXT BOX STYLES ==============
const TEXT_BOX_STYLES = [
  { id: 'handwritten', name: 'Handwritten', font: "'Fredoka', sans-serif", bg: 'transparent', border: '2px dashed #CDB4DB', color: '#1C1C1E' },
  { id: 'typewriter', name: 'Typewriter', font: "'Courier New', monospace", bg: '#FDF0D5', border: 'none', color: '#333' },
  { id: 'sticky_pink', name: 'Pink Sticky', font: "'Nunito', sans-serif", bg: '#FFC8DD', border: 'none', color: '#1C1C1E', shadow: '3px 3px 0px rgba(0,0,0,0.15)' },
  { id: 'sticky_blue', name: 'Blue Sticky', font: "'Nunito', sans-serif", bg: '#A2D2FF', border: 'none', color: '#1C1C1E', shadow: '3px 3px 0px rgba(0,0,0,0.15)' },
  { id: 'sticky_green', name: 'Green Sticky', font: "'Nunito', sans-serif", bg: '#B9FBC0', border: 'none', color: '#1C1C1E', shadow: '3px 3px 0px rgba(0,0,0,0.15)' },
  { id: 'label', name: 'Label', font: "'Fredoka', sans-serif", bg: 'white', border: '2px solid #000', color: '#1C1C1E', shadow: '2px 2px 0px rgba(0,0,0,1)' },
  { id: 'quote', name: 'Quote', font: "'Georgia', serif", bg: '#FFFAF0', border: '4px solid #CDB4DB', borderLeft: '4px solid #CDB4DB', color: '#555' },
  { id: 'banner', name: 'Banner', font: "'Fredoka', sans-serif", bg: 'linear-gradient(135deg, #FFC8DD, #A2D2FF)', border: '2px solid #000', color: '#1C1C1E' },
];

// ============== MAIN STUDIO COMPONENT ==============
export default function JournalStudio() {
  const [activeSection, setActiveSection] = useState('stickers');
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedItem, setCopiedItem] = useState(null);
  const [journalEntries, setJournalEntries] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [templateNotes, setTemplateNotes] = useState({});

  const sections = [
    { key: 'stickers', label: 'Stickers', icon: Sticker, color: '#FFC8DD' },
    { key: 'washi', label: 'Washi Tape', icon: Scissors, color: '#CDB4DB' },
    { key: 'templates', label: 'Templates', icon: Layout, color: '#A2D2FF' },
    { key: 'textboxes', label: 'Text Boxes', icon: Type, color: '#B9FBC0' },
  ];

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopiedItem(label);
    setTimeout(() => setCopiedItem(null), 1500);
  };

  // Filter stickers by search
  const filteredStickers = {};
  Object.entries(STICKER_CATEGORIES).forEach(([key, cat]) => {
    if (!searchQuery) { filteredStickers[key] = cat; return; }
    const q = searchQuery.toLowerCase();
    if (cat.label.toLowerCase().includes(q)) { filteredStickers[key] = cat; }
  });

  return (
    <div className="space-y-4" data-testid="journal-studio">
      {/* Section Tabs */}
      <div className="flex gap-2 justify-center flex-wrap">
        {sections.map(s => {
          const Icon = s.icon;
          return (
            <button key={s.key} onClick={() => setActiveSection(s.key)}
              className={`neo-btn px-4 py-2 flex items-center gap-2 ${activeSection === s.key ? '' : 'bg-white dark:bg-[#252545]'}`}
              style={activeSection === s.key ? { backgroundColor: s.color } : {}}
              data-testid={`studio-tab-${s.key}`}>
              <Icon className="w-4 h-4" />
              <span className="text-sm">{s.label}</span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      {activeSection === 'stickers' && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            className="neo-input w-full pl-10" placeholder="Search stickers, themes..." data-testid="studio-search" />
        </div>
      )}

      {/* Copied toast */}
      {copiedItem && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 neo-card px-4 py-2 bg-[#B9FBC0] animate-bounce-in">
          Copied: {copiedItem}
        </div>
      )}

      {/* ============== STICKERS ============== */}
      {activeSection === 'stickers' && (
        <div className="space-y-4">
          {Object.entries(filteredStickers).map(([key, cat]) => (
            <div key={key} className="neo-card p-4">
              <h4 className="font-bold font-['Fredoka'] mb-2">{cat.label}</h4>
              <div className="flex flex-wrap gap-2">
                {cat.stickers.map((s, i) => (
                  <button key={i} onClick={() => copyToClipboard(s, s)}
                    className="w-10 h-10 rounded-xl border-2 border-transparent hover:border-black hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] flex items-center justify-center text-2xl transition-all hover:scale-110 active:scale-95"
                    data-testid={`sticker-${key}-${i}`} title="Click to copy">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ============== WASHI TAPE ============== */}
      {activeSection === 'washi' && (
        <div className="neo-card p-4 space-y-3" style={{
          backgroundImage: 'radial-gradient(circle, #D0D0D0 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}>
          <p className="text-sm text-gray-500 dark:text-gray-400">Click a tape to copy its style name for your journal entries</p>
          {WASHI_TAPES.map(tape => (
            <button key={tape.id} onClick={() => copyToClipboard(tape.label, tape.label)}
              className="w-full h-10 rounded-sm border border-black/20 hover:border-black transition-all hover:shadow-md cursor-pointer"
              style={{ background: tape.pattern, opacity: 0.85 }}
              data-testid={`washi-${tape.id}`} title={tape.label}>
              <span className="sr-only">{tape.label}</span>
            </button>
          ))}
          {/* Decorative preview */}
          <div className="mt-4 neo-card p-6 relative overflow-hidden bg-white">
            <div className="absolute top-0 left-4 right-4 h-6" style={{ background: WASHI_TAPES[5].pattern, opacity: 0.7, transform: 'rotate(-1deg)' }} />
            <div className="absolute bottom-2 left-8 right-8 h-5" style={{ background: WASHI_TAPES[1].pattern, opacity: 0.7, transform: 'rotate(1deg)' }} />
            <p className="text-center text-gray-500 font-['Fredoka'] py-6 text-sm">Preview: Tapes on a page</p>
          </div>
        </div>
      )}

      {/* ============== TEMPLATES ============== */}
      {activeSection === 'templates' && (
        <div className="space-y-4">
          {!activeTemplate ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {JOURNAL_TEMPLATES.map(t => (
                <button key={t.id} onClick={() => { setActiveTemplate(t); setTemplateNotes({}); }}
                  className="neo-card p-4 text-center hover:scale-[1.02] transition-all"
                  style={{ backgroundColor: t.color + '30' }}
                  data-testid={`template-${t.id}`}>
                  <span className="text-3xl mb-2 block">{t.icon}</span>
                  <h4 className="font-bold font-['Fredoka'] text-sm">{t.name}</h4>
                  <p className="text-xs text-gray-500">{t.sections.length} sections</p>
                </button>
              ))}
            </div>
          ) : (
            <div className="neo-card p-6" style={{
              backgroundImage: 'radial-gradient(circle, #D0D0D0 1px, transparent 1px)',
              backgroundSize: '20px 20px'
            }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{activeTemplate.icon}</span>
                  <h3 className="text-xl font-bold font-['Fredoka']">{activeTemplate.name}</h3>
                </div>
                <button onClick={() => setActiveTemplate(null)}
                  className="neo-btn px-3 py-1 bg-gray-200 text-sm" data-testid="close-template">
                  Close
                </button>
              </div>
              <div className="space-y-4">
                {activeTemplate.sections.map((section, i) => (
                  <div key={i} className="bg-white/80 dark:bg-[#252545]/80 rounded-xl p-4 border-2 border-dashed border-gray-300">
                    <h4 className="font-bold font-['Fredoka'] text-sm mb-2" style={{ color: activeTemplate.color === '#FDF0D5' ? '#888' : activeTemplate.color }}>
                      {section}
                    </h4>
                    <textarea
                      value={templateNotes[i] || ''}
                      onChange={(e) => setTemplateNotes({ ...templateNotes, [i]: e.target.value })}
                      className="w-full bg-transparent resize-none text-sm font-['Nunito'] focus:outline-none"
                      placeholder="Write here..."
                      rows={2}
                      data-testid={`template-section-${i}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============== TEXT BOXES ============== */}
      {activeSection === 'textboxes' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">Type in any text box to try the style. Click to copy.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {TEXT_BOX_STYLES.map(style => (
              <div key={style.id}
                className="p-4 rounded-xl cursor-pointer hover:scale-[1.02] transition-all"
                style={{
                  fontFamily: style.font,
                  background: style.bg,
                  border: style.border,
                  color: style.color,
                  boxShadow: style.shadow || 'none',
                  borderLeft: style.borderLeft || style.border,
                }}
                onClick={() => copyToClipboard(style.name, style.name)}
                data-testid={`textbox-${style.id}`}>
                <p className="text-xs font-bold mb-1 opacity-60">{style.name}</p>
                <p className="text-sm" contentEditable suppressContentEditableWarning>
                  {style.id === 'quote' ? '"The secret of getting ahead is getting started."' : `Type your ${style.name.toLowerCase()} text here...`}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
